import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type Kind = "pdf" | "image" | "text" | "office" | "pptx" | "other";

function detectKind(name: string, mime?: string): Kind {
  const n = name.toLowerCase();
  if (mime?.includes("pdf") || n.endsWith(".pdf")) return "pdf";
  if (mime?.startsWith("image/") || /\.(png|jpe?g|webp|gif|svg)$/.test(n)) return "image";
  // Slides render client-side with real layout (pptx-preview).
  if (n.endsWith(".pptx")) return "pptx";
  // Other office formats render via the server-side HTML converter.
  if (/\.(docx|xlsx|xlsm|xls|csv)$/.test(n)) return "office";
  if (mime?.startsWith("text/") || /\.(txt|md|json|log|ya?ml|xml|ts|js|py)$/.test(n))
    return "text";
  return "other";
}

/** Can this file render inline (vs download-only)? */
export const canPreviewDoc = (name: string, mime?: string) =>
  detectKind(name, mime) !== "other";

/**
 * Shared document previewer (PDF / image / text / office), used by the
 * Documents page and the chat's source tiles. Rendered through a portal so
 * it always covers the screen regardless of where it's mounted.
 */
export function DocPreviewModal({
  name,
  url,
  previewUrl,
  mime,
  size,
  onClose,
}: {
  name: string;
  /** Inline-view / download URL for the raw file. */
  url: string;
  /** Server HTML-conversion URL for office formats; omit to fall back to download. */
  previewUrl?: string;
  mime?: string;
  size?: number;
  onClose: () => void;
}) {
  // When the client-side slide renderer fails, fall back to the server's
  // HTML conversion (text + images) rather than showing nothing.
  const [pptxFailed, setPptxFailed] = useState(false);
  const failPptx = useCallback(() => setPptxFailed(true), []);
  let kind = detectKind(name, mime);
  if (kind === "pptx" && pptxFailed) kind = "office";
  if (kind === "office" && !previewUrl) kind = "other";
  const [text, setText] = useState<string | null>(null);
  const [officeHtml, setOfficeHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    if (kind === "text") {
      fetch(url, { credentials: "include" })
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
        .then((t) => !cancelled && setText(t.slice(0, 200_000)))
        .catch(() => !cancelled && setError("Could not load preview."));
    } else if (kind === "office" && previewUrl) {
      fetch(previewUrl, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((d: { html: string }) => !cancelled && setOfficeHtml(d.html))
        .catch(() => !cancelled && setError("Could not convert this file for preview."));
    }
    return () => {
      cancelled = true;
    };
  }, [url, previewUrl, kind]);

  const modal = (
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/70 p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-token-lg border border-border bg-bg shadow-token-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-border bg-bg-card px-5 py-3">
          <i className="fa-solid fa-file-lines text-sm text-text-muted" />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-text-heading">
            {name}
          </span>
          {size != null && <span className="text-xs text-text-muted">{fmtSize(size)}</span>}
          <a
            href={url}
            download={name}
            title="Download"
            className="grid h-8 w-8 place-items-center rounded text-text-muted hover:bg-bg-card-hover hover:text-text-heading"
          >
            <i className="fa-solid fa-download text-sm" />
          </a>
          <button
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded text-text-muted hover:bg-bg-card-hover hover:text-text-heading"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-auto">
          {error ? (
            <DownloadFallback url={url} name={name} message={error} />
          ) : kind === "pdf" ? (
            <iframe src={url} title={name} className="h-full w-full border-0" />
          ) : kind === "image" ? (
            <div className="grid h-full place-items-center p-6">
              <img src={url} alt={name} className="max-h-full max-w-full object-contain" />
            </div>
          ) : kind === "text" ? (
            <pre className="whitespace-pre-wrap p-6 font-mono text-xs leading-relaxed text-text">
              {text ?? "Loading…"}
            </pre>
          ) : kind === "pptx" ? (
            <PptxView url={url} onFail={failPptx} />
          ) : kind === "office" ? (
            officeHtml == null ? (
              <div className="grid h-full place-items-center text-text-muted">
                <span>
                  <i className="fa-solid fa-circle-notch fa-spin mr-2" />
                  Converting…
                </span>
              </div>
            ) : (
              <div
                className="office-preview mx-auto max-w-4xl px-8 py-6 text-sm leading-relaxed text-text"
                dangerouslySetInnerHTML={{ __html: officeHtml }}
              />
            )
          ) : (
            <DownloadFallback
              url={url}
              name={name}
              message="No inline preview for this file type."
            />
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

/**
 * Generator tools (pptxgenjs etc.) often write [Content_Types].xml entries
 * for parts that aren't in the zip — pptx-preview then silently renders zero
 * slides. Drop Overrides that point at missing parts; return input untouched
 * when the manifest is already consistent.
 */
async function repairPptxManifest(data: ArrayBuffer): Promise<ArrayBuffer> {
  const { default: JSZip } = await import("jszip");
  const zip = await JSZip.loadAsync(data);
  const manifest = zip.file("[Content_Types].xml");
  if (!manifest) return data;
  const xml = await manifest.async("string");
  const fixed = xml.replace(/<Override [^>]*?\/>/g, (tag) => {
    const part = /PartName="\/([^"]+)"/.exec(tag)?.[1];
    return part && !zip.file(part) ? "" : tag;
  });
  if (fixed === xml) return data;
  zip.file("[Content_Types].xml", fixed);
  return zip.generateAsync({ type: "arraybuffer" });
}

/**
 * Faithful slide rendering via pptx-preview (lazy-loaded chunk). Slides are
 * laid out with real positioning/fonts on white slide canvases, stacked
 * vertically and scaled to the modal width.
 */
function PptxView({ url, onFail }: { url: string; onFail: () => void }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ init }, res] = await Promise.all([
          import("pptx-preview"),
          fetch(url, { credentials: "include" }),
        ]);
        if (!res.ok) throw new Error(String(res.status));
        const data = await repairPptxManifest(await res.arrayBuffer());
        const box = boxRef.current;
        if (cancelled || !box) return;
        box.innerHTML = "";
        const width = Math.min(box.clientWidth || 960, 1040);
        const previewer = init(box, {
          width,
          height: Math.round((width * 9) / 16),
        });
        await previewer.preview(data);
        // The lib can resolve without rendering anything; never show blank.
        if (!box.querySelector(".pptx-preview-slide-wrapper")) {
          throw new Error("renderer produced no slides");
        }
        if (!cancelled) setLoading(false);
      } catch {
        if (!cancelled) onFail();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, onFail]);

  return (
    <div className="min-h-full bg-bg-secondary p-4">
      {loading && (
        <div className="grid h-40 place-items-center text-text-muted">
          <span>
            <i className="fa-solid fa-circle-notch fa-spin mr-2" />
            Rendering slides…
          </span>
        </div>
      )}
      <div ref={boxRef} className="pptx-render mx-auto w-full max-w-[1040px]" />
    </div>
  );
}

function DownloadFallback({
  url,
  name,
  message,
}: {
  url: string;
  name: string;
  message: string;
}) {
  return (
    <div className="grid h-full place-items-center text-center">
      <div>
        <i className="fa-solid fa-file-arrow-down mb-3 text-3xl text-text-muted" />
        <p className="text-sm text-text-secondary">{message}</p>
        <a
          href={url}
          download={name}
          className="mt-3 inline-block rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          style={{ color: "var(--color-on-accent)" }}
        >
          <i className="fa-solid fa-download mr-1.5 text-xs" />
          Download
        </a>
      </div>
    </div>
  );
}
